import { isAfter, isSameDay } from 'date-fns';
import React, { forwardRef, useMemo, useState } from 'react';
import { TimeslotContextMenu } from 'shared/components/connected/timeslot-context-menu';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { InteractionType } from 'shared/types/data-type';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';

import { CompleteList } from './complete-list';
import { DateList } from './date-list';
import { EditList } from './edit-list';
import { LifecycleList } from './lifecycle-list';
import { TimeslotList } from './timeslot-list';

export type TaskCardContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside'
> & {
  task: Task;
  onClose: () => void;
  nameRef?: React.RefObject<HTMLInputElement>;
  hideMeta?: boolean;
  occurrenceId?: ID;
  timeslotDate?: Timestamp;
};

export const TaskCardContextMenu = forwardRef<
  HTMLDivElement,
  TaskCardContextMenuProps
>(
  (
    {
      task,
      onClose,
      referenceElement,
      location,
      nameRef,
      hideMeta,
      occurrenceId,
      timeslotDate,
    },
    ref,
  ) => {
    const [timeslotMenuOpen, setTimeslotMenuOpen] = useState(false);
    const today = useToday();

    const isSubtask = !!task.parentIds?.length;
    const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);
    const isFutureDate = useMemo(
      () =>
        timeslotDate &&
        !isSameDay(timeslotDate, today) &&
        isAfter(timeslotDate, today),
      [timeslotDate, today],
    );

    const onChangeTimeslot = () => setTimeslotMenuOpen(true);

    return timeslotMenuOpen ? (
      <TimeslotContextMenu
        ref={ref}
        location={location}
        referenceElement={referenceElement}
        position="bottom-start"
        task={task}
        onClose={onClose}
        representedDay={timeslotDate!}
      />
    ) : (
      <PopupMenu
        ref={ref}
        location={location}
        referenceElement={referenceElement}
        onOutside={onClose}
        interactionType={InteractionType.Task}
        position="bottom-start"
      >
        {isSubtask && <LifecycleList task={task} onClose={onClose} />}
        {!isSubtask &&
          (isHabit && isFutureDate ? (
            <>
              <EditList task={task} onClose={onClose} nameRef={nameRef} />
              <TimeslotList
                task={task}
                date={timeslotDate!}
                id={occurrenceId}
                onClose={onClose}
                onChangeTimeslot={onChangeTimeslot}
              />
              <LifecycleList task={task} onClose={onClose} hasListBelow />
              {/*<HabitList onClose={onClose} task={task} />*/}
            </>
          ) : (
            <>
              <EditList task={task} onClose={onClose} nameRef={nameRef} />
              {!!timeslotDate && (
                <TimeslotList
                  task={task}
                  date={timeslotDate}
                  id={occurrenceId}
                  onClose={onClose}
                  onChangeTimeslot={onChangeTimeslot}
                />
              )}
              {!isHabit && !hideMeta && (
                <DateList task={task} onClose={onClose} />
              )}
              <CompleteList task={task} onClose={onClose} date={timeslotDate} />
              <LifecycleList
                task={task}
                onClose={onClose}
                hasListBelow={isHabit}
              />
              {/*{isHabit && <HabitList onClose={onClose} task={task} />}*/}
            </>
          ))}
      </PopupMenu>
    );
  },
);
